import React, { useState, useEffect } from "react";
import { Plus, RefreshCw, X, Pencil, Trash2 } from "lucide-react";
import { FaBuilding, FaSwimmingPool, FaTableTennis, FaDumbbell, FaTheaterMasks, FaTree, FaCog } from "react-icons/fa";
import Modal from './Modal'
import { API_BASE } from '../config'

async function apiFetch(path, opts = {}) {
  const token = sessionStorage.getItem("id_token");
  const resp = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...opts.headers },
  });
  if (!resp.ok) throw new Error(`API ${resp.status}`);
  return resp.json();
}

const ICON_OPTIONS = [
  { value: "clubhouse", label: "Clubhouse", icon: FaBuilding },
  { value: "swimming_pool", label: "Swimming Pool", icon: FaSwimmingPool },
  { value: "gym", label: "Gym", icon: FaDumbbell },
  { value: "party_hall", label: "Party Hall", icon: FaTheaterMasks },
  { value: "indoor_games", label: "Indoor Games", icon: FaTableTennis },
  { value: "garden", label: "Garden", icon: FaTree },
];

const ICON_MAP = {
  clubhouse: FaBuilding,
  swimming_pool: FaSwimmingPool,
  gym: FaDumbbell,
  party_hall: FaTheaterMasks,
  indoor_games: FaTableTennis,
  garden: FaTree,
};

function getIcon(iconKey) {
  return ICON_MAP[iconKey] || FaCog;
}

export default function AdminAmenitiesManagement() {
  const [amenities, setAmenities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const communityId = sessionStorage.getItem("community_id") || "";

  const [form, setForm] = useState({ name: "", icon: "clubhouse" });

  function showToast(message, type = "success") {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3000);
  }

  async function loadAmenities() {
    if (!communityId) return;
    setLoading(true);
    try {
      const data = await apiFetch(`/amenities?communityId=${communityId}`);
      setAmenities(data || []);
    } catch (err) {
      console.error("loadAmenities error:", err);
      showToast("Failed to load amenities", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadAmenities(); }, [communityId]);

  function openAdd() {
    setEditItem(null);
    setForm({ name: "", icon: "clubhouse" });
    setShowForm(true);
  }

  function openEdit(item) {
    setEditItem(item);
    setForm({ name: item.NAME || "", icon: item.ICON || "clubhouse" });
    setShowForm(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) {
      showToast("Please enter amenity name", "error");
      return;
    }
    setSubmitting(true);
    try {
      if (editItem) {
        await apiFetch("/amenities", {
          method: "PATCH",
          body: JSON.stringify({
            COMMUNITY_ID: communityId,
            AMENITY_ID: editItem.AMENITY_ID,
            NAME: form.name.trim(),
            ICON: form.icon,
          }),
        });
        showToast("Amenity updated");
      } else {
        await apiFetch("/amenities", {
          method: "POST",
          body: JSON.stringify({
            COMMUNITY_ID: communityId,
            NAME: form.name.trim(),
            ICON: form.icon,
          }),
        });
        showToast("Amenity added");
      }
      setShowForm(false);
      setEditItem(null);
      loadAmenities();
    } catch (err) {
      console.error("submit error:", err);
      showToast(editItem ? "Failed to update" : "Failed to add", "error");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(amenityId) {
    try {
      await apiFetch(`/amenities?communityId=${encodeURIComponent(communityId)}&amenityId=${encodeURIComponent(amenityId)}`, {
        method: "DELETE",
      });
      showToast("Amenity deleted");
      setDeleteConfirm(null);
      loadAmenities();
    } catch (err) {
      console.error("delete error:", err);
      showToast("Failed to delete amenity", "error");
    }
  }

  async function handleToggleStatus(item) {
    const newStatus = item.STATUS === "active" ? "inactive" : "active";
    try {
      await apiFetch("/amenities", {
        method: "PATCH",
        body: JSON.stringify({
          COMMUNITY_ID: communityId,
          AMENITY_ID: item.AMENITY_ID,
          STATUS: newStatus,
        }),
      });
      showToast(`Amenity ${newStatus === "active" ? "enabled" : "disabled"}`);
      loadAmenities();
    } catch (err) {
      console.error("toggle error:", err);
      showToast("Failed to update status", "error");
    }
  }

  return (
    <div className="p-4">
      {/* Toasts */}
      <div className="fixed top-4 right-4 z-[100] space-y-2">
        {toasts.map((t) => (
          <div key={t.id} className={`px-4 py-2 rounded-lg shadow-lg text-white text-sm ${t.type === "error" ? "bg-red-500" : "bg-green-500"}`}>
            {t.message}
          </div>
        ))}
      </div>

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <Modal title="Delete Amenity?" onClose={() => setDeleteConfirm(null)}>
          <p className="text-sm text-gray-500 mb-4">
            This will permanently remove <strong>{deleteConfirm.NAME}</strong>. Existing bookings for this amenity won't be affected.
          </p>
          <div className="flex justify-end gap-2">
            <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg">
              Cancel
            </button>
            <button onClick={() => handleDelete(deleteConfirm.AMENITY_ID)} className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg">
              Delete
            </button>
          </div>
        </Modal>
      )}

      {/* Add/Edit Modal */}
      {showForm && (
        <Modal title={editItem ? "Edit Amenity" : "Add Amenity"} onClose={() => setShowForm(false)}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amenity Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Swimming Pool"
                className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Icon *</label>
              <div className="grid grid-cols-3 gap-2">
                {ICON_OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  const selected = form.icon === opt.value;
                  return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setForm({ ...form, icon: opt.value })}
                        className={`flex flex-col items-center gap-1 p-3 rounded-xl text-xs border-2 transition-colors ${
                          selected ? "border-blue-500 bg-blue-50 text-blue-700" : "border-gray-200 text-gray-600"
                        }`}
                      >
                        <Icon size={20} />
                        <span>{opt.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg">
                  Cancel
                </button>
                <button type="submit" disabled={submitting} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg disabled:opacity-50">
                  {submitting ? "Saving..." : editItem ? "Update" : "Add Amenity"}
                </button>
              </div>
            </form>
        </Modal>
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <h2 className="text-lg font-bold">Manage Amenities</h2>
          <p className="text-xs text-gray-500">Add, edit, or remove amenities for your community</p>
        </div>
        <button onClick={loadAmenities} className="p-2 rounded-lg bg-gray-100 active:bg-gray-200">
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Add Button */}
      <button
        onClick={openAdd}
        className="w-full bg-blue-600 text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 mb-4 active:bg-blue-700"
      >
        <Plus size={18} /> Add Amenity
      </button>

      {/* Amenities List */}
      {loading ? (
        <div className="text-center py-8 text-gray-400">Loading...</div>
      ) : amenities.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <FaBuilding size={28} className="mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No amenities configured</p>
          <p className="text-xs mt-1">Tap "Add Amenity" to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {amenities.map((item) => {
            const Icon = getIcon(item.ICON);
            const isInactive = item.STATUS === "inactive";
            return (
              <div key={item.AMENITY_ID} className={`bg-white border rounded-xl p-4 flex items-center gap-3 ${isInactive ? "border-gray-200 opacity-60" : "border-gray-200"}`}>
                <div className={`p-2 rounded-lg flex-shrink-0 ${isInactive ? "bg-gray-100 text-gray-400" : "bg-blue-100 text-blue-700"}`}>
                  <Icon size={22} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-gray-900">{item.NAME}</h3>
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${isInactive ? "bg-gray-100 text-gray-500" : "bg-green-100 text-green-700"}`}>
                    {isInactive ? "Inactive" : "Active"}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleToggleStatus(item)}
                    className={`px-2 py-1.5 rounded-lg text-xs font-medium ${isInactive ? "bg-green-50 text-green-700" : "bg-gray-50 text-gray-600"}`}
                  >
                    {isInactive ? "Enable" : "Disable"}
                  </button>
                  <button onClick={() => openEdit(item)} className="p-1.5 rounded-lg bg-gray-50 text-gray-500 active:bg-gray-100">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => setDeleteConfirm(item)} className="p-1.5 rounded-lg bg-red-50 text-red-500 active:bg-red-100">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
